import { route } from 'preact-router';
import { useState, useEffect } from 'preact/hooks';
import { useAuth } from '../lib/auth';

// Developer workspace ID - only this workspace sees super-admin link
const DEVELOPER_WORKSPACE_ID = 'T925XKE90';

export function Sidebar({ open, onClose, workspace, currentPath }) {
  const { subscription, user } = useAuth();
  // Use state to track current path for reactivity
  const [activePath, setActivePath] = useState(currentPath || '/');

  // Update when prop changes or on route change
  useEffect(() => {
    if (currentPath) {
      setActivePath(currentPath);
    }
  }, [currentPath]);

  const isOwnerOrAdmin = user?.role === 'owner' || user?.role === 'admin';
  const isDeveloperWorkspace = workspace?.id === DEVELOPER_WORKSPACE_ID;

  const navItems = [
    { path: '/', label: 'Dashboard', icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6' },
    { path: '/wallchart', label: 'Wallchart', icon: 'M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2' },
    { path: '/team', label: 'Team', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { path: '/requests', label: 'Requests', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01' },
    { path: '/calendar', label: 'Calendar', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { path: '/settings', label: 'Settings', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z' },
    // Billing - only for owners/admins
    ...(isOwnerOrAdmin ? [{ path: '/billing', label: 'Billing', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' }] : [])
  ];

  function isActive(itemPath) {
    if (itemPath === '/') {
      return activePath === '/';
    }
    return activePath.startsWith(itemPath);
  }

  function handleNavClick(path, e) {
    e.preventDefault();
    setActivePath(path);
    route(path);
    onClose();
  }

  return (
    <>
      <div
        class={`sidebar-overlay ${open ? 'visible' : ''}`}
        onClick={onClose}
      />
      <aside class={`sidebar ${open ? 'open' : ''}`}>
        <div class="sidebar-header">
          <div class="sidebar-logo">
            {workspace?.icon ? (
              <img src={workspace.icon} alt="" style="width: 100%; height: 100%; border-radius: 8px;" />
            ) : (
              'V'
            )}
          </div>
          <div class="sidebar-workspace">
            <h2>{workspace?.name || 'Vacation Tracker'}</h2>
            <span>{workspace?.plan || 'Free'} Plan</span>
          </div>
        </div>

        <nav class="sidebar-nav">
          {navItems.map(item => (
            <a
              key={item.path}
              href={item.path}
              class={isActive(item.path) ? 'active' : ''}
              onClick={(e) => handleNavClick(item.path, e)}
            >
              <svg
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d={item.icon} />
              </svg>
              {item.label}
            </a>
          ))}
        </nav>

        {/* Developer Admin Link - only for developer workspace owners/admins */}
        {isDeveloperWorkspace && isOwnerOrAdmin && (
          <div class="sidebar-dev-admin">
            <a
              href="/super-admin"
              class="dev-admin-link"
              onClick={(e) => { e.preventDefault(); window.location.href = '/super-admin'; }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span>App Admin Panel</span>
              <svg class="arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </a>
          </div>
        )}

        {/* Upgrade CTA - show for trial or when not on pro/business */}
        {subscription && (subscription.status === 'trialing' || subscription.plan === 'trial' || subscription.plan === 'free') && (
          <div class="sidebar-upgrade">
            <div class="upgrade-card">
              <div class="upgrade-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                </svg>
              </div>
              <div class="upgrade-text">
                <strong>Upgrade to Pro</strong>
                <span>Unlock all features</span>
              </div>
              <a
                href="/upgrade"
                class="upgrade-button"
                onClick={(e) => handleNavClick('/upgrade', e)}
              >
                View Plans
              </a>
            </div>
          </div>
        )}

        <style>{`
          .sidebar-upgrade {
            padding: 1rem;
            margin-top: auto;
          }

          .upgrade-card {
            background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
            border-radius: 12px;
            padding: 1rem;
            color: white;
            text-align: center;
          }

          .upgrade-icon {
            width: 40px;
            height: 40px;
            background: rgba(255,255,255,0.2);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 0.75rem;
          }

          .upgrade-icon svg {
            width: 20px;
            height: 20px;
          }

          .upgrade-text {
            margin-bottom: 0.75rem;
          }

          .upgrade-text strong {
            display: block;
            font-size: 0.875rem;
            margin-bottom: 0.25rem;
          }

          .upgrade-text span {
            font-size: 0.75rem;
            opacity: 0.8;
          }

          .upgrade-button {
            display: block;
            background: white;
            color: #4F46E5;
            padding: 0.5rem 1rem;
            border-radius: 6px;
            font-size: 0.8125rem;
            font-weight: 600;
            text-decoration: none;
            transition: transform 0.2s, box-shadow 0.2s;
          }

          .upgrade-button:hover {
            transform: translateY(-1px);
            box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          }

          .sidebar-dev-admin {
            padding: 0.5rem 1rem;
            border-top: 1px solid #e5e7eb;
          }

          .dev-admin-link {
            display: flex;
            align-items: center;
            gap: 0.75rem;
            padding: 0.75rem 1rem;
            background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
            border-radius: 8px;
            color: #f1f5f9;
            text-decoration: none;
            font-size: 0.875rem;
            font-weight: 500;
            transition: all 0.2s;
          }

          .dev-admin-link:hover {
            background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
            transform: translateX(2px);
          }

          .dev-admin-link svg {
            width: 18px;
            height: 18px;
            flex-shrink: 0;
          }

          .dev-admin-link span {
            flex: 1;
          }

          .dev-admin-link .arrow {
            width: 16px;
            height: 16px;
            opacity: 0.6;
          }
        `}</style>
      </aside>
    </>
  );
}
